import { useMemo } from "react";
import {
  createRouter,
  RouterProvider,
  createRoute,
  createRootRoute,
  Outlet,
} from "@tanstack/react-router";
import { useGlobalContext } from "../../../utils/GlobalContext.ts";
import { StandardPageProviders } from "../../pages/StandardPageProviders/StandardPageProviders.tsx";
import { ForwardToLocaleRoute } from "../../pages/ForwardToLocaleRoute/ForwardToLocaleRoute.tsx";
import { NotFound } from "../../pages/NotFound/NotFound.tsx";
import { ContactUsPage } from "../../pages/ContactUsPage/ContactUsPage.tsx";
import { SynopsisPage } from "../../pages/SynopsisPage";
import { OneTrustLoader } from "../OneTrustLoader/OneTrustLoader.tsx";

function RootRouteComponent() {
  return (
    <>
      <Outlet />
      <OneTrustLoader />
    </>
  );
}

export const Router = () => {
  // Use context directly to avoid throwing error if not available
  const globalContext = useGlobalContext();

  const { localeConfig } = globalContext || { localeConfig: { locales: [] } };

  const router = useMemo(
    () => initRouter(localeConfig.locales),
    [localeConfig.locales],
  );

  // router.subscribe("onResolved", trackPageView);

  // If no global context, this should not happen as we handle it in main.tsx
  // But adding safety check just in case
  if (!globalContext) {
    console.warn("Router: GlobalContext not available");
    return null;
  }

  return <RouterProvider router={router} defaultNotFoundComponent={NotFound} />;
};

const initRouter = (supportedLocales: ReadonlyArray<string>) => {
  // Create root route
  const rootRoute = createRootRoute({ component: RootRouteComponent });

  // Create locale-specific routes
  const localeRoutes = supportedLocales
    .map((l) => l.toLowerCase())
    .map((localeCode) => {
      // Create locale route
      const localeRoute = createRoute({
        getParentRoute: () => rootRoute,
        path: `/${localeCode.toLowerCase()}`,
      });

      // Create index route for locale
      const indexRoute = createRoute({
        getParentRoute: () => localeRoute,
        path: "/",
        component: StandardPageProviders,
      });

      // Static contact-us route. Registered before the catch-all `$slug`
      // route so the dedicated form page wins precedence over CMS lookups.
      const contactUsRoute = createRoute({
        getParentRoute: () => localeRoute,
        path: "/contact-us",
        component: ContactUsPage,
      });

      const synopsisRoute = createRoute({
        getParentRoute: () => localeRoute,
        path: "/synopsis",
        component: SynopsisPage,
      });

      // Create dynamic slug route for locale
      const slugRoute = createRoute({
        getParentRoute: () => localeRoute,
        path: "/$slug",
        component: StandardPageProviders,
      });

      // Create 404 route for locale
      const notFoundRoute = createRoute({
        getParentRoute: () => localeRoute,
        path: "*",
        component: NotFound,
      });

      return {
        localeRoute,
        indexRoute,
        contactUsRoute,
        synopsisRoute,
        slugRoute,
        notFoundRoute,
      };
    });

  // Create root index route for locale detection
  const rootIndexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    component: ForwardToLocaleRoute,
  });

  // Create fallback route for any unmatched paths
  const fallbackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "*",
    component: ForwardToLocaleRoute,
  });

  // Flatten all routes
  const allRoutes = [
    rootIndexRoute, // Add root index route first
    ...localeRoutes.flatMap(
      ({
        localeRoute,
        indexRoute,
        contactUsRoute,
        synopsisRoute,
        slugRoute,
        notFoundRoute,
      }) => [
        localeRoute,
        indexRoute,
        contactUsRoute,
        synopsisRoute,
        slugRoute,
        notFoundRoute,
      ],
    ),
    fallbackRoute,
  ];

  const routeTree = rootRoute.addChildren(allRoutes);
  return createRouter({ routeTree });
};
